import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { toast } from '../components/Toast';

const ParentsReport = () => {
    const [parents, setParents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedParent, setSelectedParent] = useState(null);
    const [parentChildren, setParentChildren] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        loadParents();
    }, []);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const loadParents = async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/users/parents');
            // New endpoint returns phone and student_count directly
            setParents(res.data || []);
        } catch (e) {
            toast.error(e.message || "Failed loading parents");
        } finally {
            setLoading(false);
        }
    };

    const handleViewChildren = async (parent) => {
        setSelectedParent(parent);
        try {
            const res = await apiFetch(`/parents/${parent.user_id}/students`);
            if (res.status === 'error') {
                throw new Error(res.message || 'Failed to load children');
            }
            const children = (res.data || []).sort((a, b) => 
                (a.full_name || '').localeCompare(b.full_name || '')
            );
            setParentChildren(children);
            setShowModal(true);
        } catch (e) {
            console.error('View children error:', e);
            toast.error(e.message || "Failed loading children");
        }
    };

    const filteredParents = parents
        .filter(p => 
            (p.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
            (p.phone || '').toLowerCase().includes(search.toLowerCase()) ||
            (p.email || '').toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));

    // Pagination
    const totalPages = Math.ceil(filteredParents.length / itemsPerPage);
    const paginatedParents = filteredParents.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div>
            <h1 className="page-title text-gradient" style={{ margin: 0, marginBottom: '24px' }}>Parents Report</h1>

            <div style={{ marginBottom: '16px' }}>
                <input
                    type="text"
                    placeholder="Search by name, phone or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '6px', 
                        width: '300px', 
                        height: '40px' 
                    }}
                />
            </div>

            <div className="glass-panel" style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ fontSize: '14px', width: '100%' }}>
                    <thead>
                        <tr style={{ height: '48px' }}>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Parent Name</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Phone</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Email</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>No. of Children</th>
                            <th style={{ padding: '10px 16px', textAlign: 'left' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr style={{ height: '48px' }}>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '10px 16px' }}>Loading parents...</td>
                            </tr>
                        ) : filteredParents.length === 0 ? (
                            <tr style={{ height: '48px' }}>
                                <td colSpan="5" style={{ textAlign: 'center', padding: '10px 16px' }}>No parents found.</td>
                            </tr>
                        ) : (
                            paginatedParents.map((p) => (
                                <tr key={p.user_id} style={{ height: '48px' }}>
                                    <td style={{ padding: '10px 16px', fontWeight: '600' }}>{p.full_name}</td>
                                    <td style={{ padding: '10px 16px' }}>{p.phone || '-'}</td>
                                    <td style={{ padding: '10px 16px' }}>{p.email || '-'}</td>
                                    <td style={{ padding: '10px 16px' }}>{p.student_count || 0}</td>
                                    <td style={{ padding: '10px 16px' }}>
                                        <button 
                                            className="btn-secondary"
                                            onClick={() => handleViewChildren(p)}
                                            style={{ padding: '4px 12px', fontSize: '12px' }}
                                        >
                                            View Children
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {filteredParents.length > 0 && (
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: "16px",
                }}>
                    <span style={{ fontSize: "13px", color: "#666" }}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, filteredParents.length)} of{" "}
                        {filteredParents.length} parents
                    </span>

                    <div style={{ display: "flex", gap: "8px" }}>
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            style={{
                                padding: "6px 14px",
                                border: "1px solid #ddd",
                                borderRadius: "6px",
                                background: currentPage === 1 ? "#f5f5f5" : "#fff",
                                color: currentPage === 1 ? "#999" : "#000",
                                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                                fontSize: "13px",
                            }}
                        >
                            ← Prev
                        </button>

                        <span style={{
                            padding: "6px 14px",
                            border: "1px solid #ddd",
                            borderRadius: "6px",
                            fontSize: "13px",
                            background: "#000",
                            color: "#fff",
                        }}>
                            {currentPage} / {totalPages}
                        </span>

                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            style={{
                                padding: "6px 14px",
                                border: "1px solid #ddd",
                                borderRadius: "6px",
                                background: currentPage === totalPages ? "#f5f5f5" : "#fff",
                                color: currentPage === totalPages ? "#999" : "#000",
                                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                                fontSize: "13px",
                            }}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}

            {/* Children Modal */}
            {showModal && selectedParent && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="glass-panel" style={{ 
                        padding: '24px', 
                        maxWidth: '700px', 
                        width: '90%', 
                        maxHeight: '80vh',
                        overflow: 'auto',
                        background: '#fff'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            marginBottom: '16px' 
                        }}>
                            <h3 style={{ margin: 0 }}>Children of {selectedParent.full_name}</h3>
                            <button 
                                onClick={() => setShowModal(false)}
                                style={{ 
                                    fontSize: '20px', 
                                    background: 'none', 
                                    border: 'none', 
                                    cursor: 'pointer' 
                                }}
                            >
                                ×
                            </button>
                        </div>
                        
                        {parentChildren.length === 0 ? (
                            <p>No children found for this parent.</p>
                        ) : (
                            <table className="data-table" style={{ fontSize: '14px', width: '100%' }}>
                                <thead>
                                    <tr style={{ height: '40px' }}>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Student Name</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Registration</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Grade</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Route</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Stop</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Bus Code</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {parentChildren.map((child) => (
                                        <tr key={child.student_id} style={{ height: '40px' }}>
                                            <td style={{ padding: '8px 12px' }}>{child.full_name}</td>
                                            <td style={{ padding: '8px 12px' }}>{child.registration_number || '-'}</td>
                                            <td style={{ padding: '8px 12px' }}>{child.grade || '-'}</td>
                                            <td style={{ padding: '8px 12px' }}>{child.route_code || child.route_name || '-'}</td>
                                            <td style={{ padding: '8px 12px' }}>{child.stop_name || child.stop?.stop_name || '-'}</td>
                                            <td style={{ padding: '8px 12px' }}>{child.bus_code || child.vehicle_code || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        
                        <div style={{ marginTop: '16px', textAlign: 'right' }}>
                            <button 
                                className="btn-secondary"
                                onClick={() => setShowModal(false)}
                                style={{ height: '40px' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParentsReport;
